import { Box, BoxProps, Theme, Tooltip, TooltipProps } from "@mui/material";
import { forwardRef } from "react";

const enterDelayMapping = {
  none: { enterDelay: 0, enterNextDelay: 0, enterTouchDelay: 0 },
  default: { enterDelay: 100, enterNextDelay: 0, enterTouchDelay: 700 }, // MUI default
  small: { enterDelay: 300, enterNextDelay: 300, enterTouchDelay: 700 },
  medium: { enterDelay: 500, enterNextDelay: 500, enterTouchDelay: 700 },
  large: { enterDelay: 700, enterNextDelay: 700, enterTouchDelay: 700 },
};

export interface AppTooltipProps extends TooltipProps {
  /** If `true` - children are wrapped, `false` - children are rendered directly.
   * @default true
   */
  isEnabled?: boolean;
  // TODO: remove this and use AppTypography (not here but on the places where helpText tooltip is rendered)
  /** @deprecated use AppTypography */
  variant?: "default" | "helpText";
  isInline?: boolean;
  /** Use default enter delays preset (small) where tooltip is shown after small delay, not instantly. */
  isDefaultEnterDelays?: boolean;
  /** Preset for enter delays. */
  enterDelayPreset?: "none" | "default" | "small" | "medium" | "large";
  /** Props for element that wraps the children. */
  wrapperProps?: BoxProps;
  children: TooltipProps["children"];
}

/** Extends MUI Tooltip with custom logic. */
export default forwardRef<unknown, AppTooltipProps>(function AppTooltip(
  {
    enterDelay,
    enterNextDelay,
    enterTouchDelay,
    isEnabled = true,
    variant = "default",
    isInline,
    isDefaultEnterDelays,
    enterDelayPreset,
    wrapperProps,
    children,
    ...tooltipProps
  }: AppTooltipProps,
  ref,
) {
  const enterDelayPresetComputed = enterDelayPreset || isDefaultEnterDelays ? "small" : "default";
  const enterDelays = enterDelayMapping[enterDelayPresetComputed];

  if (!isEnabled) {
    return children ? <>{children}</> : null;
  }

  return (
    <Tooltip
      ref={ref}
      placement={variant === "helpText" ? "bottom-start" : undefined}
      enterDelay={enterDelays.enterDelay}
      enterNextDelay={enterDelays.enterNextDelay}
      enterTouchDelay={enterDelays.enterTouchDelay}
      {...tooltipProps}
      sx={{ ...tooltipProps.sx, ...(isInline && { display: "inline" }) }}
    >
      {variant === "helpText" ? (
        <Box
          component='div'
          {...wrapperProps}
          sx={
            {
              ...(variant === "helpText" && {
                textDecoration: "underline dotted 2px",
                textDecorationColor: (th: Theme) => th.palette.divider,
                "& *": {
                  textDecoration: "underline dotted 2px",
                  textDecorationColor: (th: Theme) => th.palette.divider,
                },
              }),
              ...tooltipProps.sx,
              ...wrapperProps?.sx,
              ...(isInline && { display: "inline" }),
            } as BoxProps["sx"]
          }
        >
          {children}
        </Box>
      ) : (
        children
      )}
    </Tooltip>
  );
});
